/**
 * Resolves the effective AI tier for the current chat + workspace, and
 * exposes the switcher's read/write surface.
 *
 * Precedence (docs/pricing/line-tier-integration-plan.md §3a):
 *   1. `chatSession.tierAtStart` — the tier the thread was created under
 *   2. `localStorage[TIER_STORAGE_KEY_PREFIX + workspaceId]` — user's last-used tier for THIS workspace
 *   3. Plan's `aiDefaultTier` (from pricingCatalog.json)
 *   4. `DEFAULT_AI_TIER` ('line')
 *
 * `availableTiers` reflects the plan's `aiTierAccess` map — the switcher
 * uses this to render locked chips with a padlock. Legacy plans (no access
 * map yet) fall back to `['line']` so the flag-off world sees exactly the
 * old behavior.
 */

import { useCallback, useEffect, useMemo } from 'react'
import { useAiChat } from '../context/useAiChat'
import { TIER_STORAGE_KEY_PREFIX } from '../context/AiChatContext'
import { DEFAULT_AI_TIER, type AiTierId } from '../types/ai'
import { AI_TIER_CATALOG, isAiTierId, TIER_ORDER } from '../utils/aiTierCatalog'
import { PRICING_CATALOG } from '../components/Settings/pricingConfig'
import type { PricingPlan } from '../components/Settings/pricingConfig'
import { testingOverrideAllowsAllFeatures } from '../config/testingOverrides'

const readWorkspaceLastUsed = (workspaceId: string | null | undefined): AiTierId | null => {
  if (!workspaceId || typeof window === 'undefined') return null
  const raw = window.localStorage.getItem(`${TIER_STORAGE_KEY_PREFIX}${workspaceId}`)
  return isAiTierId(raw) ? raw : null
}

const writeWorkspaceLastUsed = (workspaceId: string | null | undefined, tier: AiTierId) => {
  if (!workspaceId || typeof window === 'undefined') return
  window.localStorage.setItem(`${TIER_STORAGE_KEY_PREFIX}${workspaceId}`, tier)
}

const findPlan = (planId: string | null | undefined): PricingPlan | null => {
  if (!planId) return null
  return (
    PRICING_CATALOG.teamPlans.find((plan) => plan.id === planId)
      ?? PRICING_CATALOG.personalPlans.find((plan) => plan.id === planId)
      ?? null
  )
}

export interface UseAiTierOptions {
  /** Current workspace. Null when the user hasn't picked a workspace yet. */
  workspaceId: string | null | undefined
  /** Active plan id. Passed by the surface that knows the user's plan. */
  planId?: string | null
  /** Tier the current chat session was created with, if reopening an existing chat. */
  chatTierAtStart?: AiTierId | null
  /** Demo unlock (R35): the workspace's plan gates are lifted, so every tier is available. */
  limitsLifted?: boolean
}

export interface UseAiTierResult {
  /** Effective tier for the current chat. Never null after mount. */
  tier: AiTierId
  /** Tier ids this plan is allowed to select. Ordered `['line','line_pro','line_max']`. */
  availableTiers: readonly AiTierId[]
  /** Tier ids this plan cannot select — used for locked-chip padlocks. */
  lockedTiers: readonly AiTierId[]
  /** Plan default tier — the switcher lands here on first render for this plan. */
  defaultTier: AiTierId
  /** True when a tier the user is asking about is locked on this plan. */
  isTierLocked: (tier: AiTierId) => boolean
  /** Change tier. Writes to context + workspace-scoped localStorage. */
  setTier: (tier: AiTierId) => void
  /**
   * True when the plan definition itself is missing an `aiTierAccess` map —
   * caller should treat this as a flag-off shim: only `line` is offered and
   * the switcher UI should hide itself.
   */
  isLegacyPlan: boolean
}

export const useAiTier = ({
  workspaceId,
  planId,
  chatTierAtStart,
  limitsLifted = false,
}: UseAiTierOptions): UseAiTierResult => {
  const { tier: contextTier, setTier: setContextTier } = useAiChat()
  const plan = findPlan(planId ?? null)
  const availableTiers = useMemo<readonly AiTierId[]>(() => {
    // Testing bypass — every tier unlocked regardless of plan (see
    // src/config/testingOverrides.ts). Flip that flag to `false` before
    // shipping to real users.
    if (limitsLifted || testingOverrideAllowsAllFeatures()) return TIER_ORDER
    const access = plan?.aiTierAccess
    if (!access || access.length === 0) return ['line']
    return TIER_ORDER.filter((tier) => access.includes(tier))
  }, [limitsLifted, plan])

  const isLegacyPlan = !plan || !plan.aiTierAccess
  const defaultTier: AiTierId = plan?.aiDefaultTier ?? DEFAULT_AI_TIER

  const resolvedInitial = useMemo<AiTierId>(() => {
    if (chatTierAtStart && availableTiers.includes(chatTierAtStart)) return chatTierAtStart
    const lastUsed = readWorkspaceLastUsed(workspaceId)
    if (lastUsed && availableTiers.includes(lastUsed)) return lastUsed
    if (availableTiers.includes(defaultTier)) return defaultTier
    return availableTiers[0] ?? DEFAULT_AI_TIER
  }, [availableTiers, chatTierAtStart, defaultTier, workspaceId])

  // Sync context on first mount and whenever a locked tier becomes selected
  // externally (e.g. plan downgrade mid-session) — the switcher UI needs the
  // context to reflect an available tier, never a locked one.
  useEffect(() => {
    const current = contextTier ?? resolvedInitial
    if (availableTiers.includes(current)) {
      if (!contextTier) setContextTier(current)
      return
    }
    // Current tier is now locked → downgrade to a legal one.
    const fallback = availableTiers.includes(defaultTier)
      ? defaultTier
      : (availableTiers[0] ?? DEFAULT_AI_TIER)
    setContextTier(fallback)
  }, [availableTiers, contextTier, defaultTier, resolvedInitial, setContextTier])

  const effectiveTier: AiTierId = contextTier && availableTiers.includes(contextTier)
    ? contextTier
    : resolvedInitial

  const lockedTiers = useMemo<readonly AiTierId[]>(
    () => TIER_ORDER.filter((tier) => !availableTiers.includes(tier)),
    [availableTiers],
  )

  const setTier = useCallback((next: AiTierId) => {
    if (!availableTiers.includes(next)) return
    setContextTier(next)
    writeWorkspaceLastUsed(workspaceId, next)
  }, [availableTiers, setContextTier, workspaceId])

  const isTierLocked = useCallback(
    (candidate: AiTierId) => !availableTiers.includes(candidate),
    [availableTiers],
  )

  return {
    tier: effectiveTier,
    availableTiers,
    lockedTiers,
    defaultTier,
    isTierLocked,
    setTier,
    isLegacyPlan,
  }
}

/** Convenience re-export so components can grab the descriptor without a second import. */
export { AI_TIER_CATALOG }
